import * as Axios from 'axios';
import * as API from '../index';
import { getToken } from '../../security/utils';
import * as Mappings from './mappings';
import {
  IDownloadableFile,
  DownloadableFileDefault,
  ErrorDataResponse,
  ErrorDataResponseDefault,
  Error404,
  Error401,
  Error403,
  Error500,
  MyKeysPromise,
} from '../../../interfaces';
import { convertBlobToJson } from '../../../utils';
import Logger from '../../../libs/logger';  import { LogArea } from '../../../libs/logger/const';

export const Map = Mappings;

const apiBaseUrl = process.env.REACT_APP_MYKEYS_API_BASE_URL;
const defaultVersion = process.env.REACT_APP_MYKEYS_API_DEFAULT_VERSION;
const baseUri = `${apiBaseUrl}api/`;

const getVersion = (version: string | undefined): string => {
  const v = version ?? defaultVersion;
  const isVersionLess = version === '' || !v;
  if (isVersionLess) return '';
  else return `${v}/`;
}

const buildUri = (apiRoute: string, apiVersion: string | undefined): string => { 
  const v = getVersion(apiVersion);
  return `${baseUri}${v}${apiRoute}`;
 }

async function addAuthorizationHeader(
  reqConfig: Axios.AxiosRequestConfig = {}
) {
  
  const token = await getToken();
  if (!reqConfig.headers) {
    reqConfig.headers = {};
  }
  reqConfig.headers.Authorization = `Bearer ${token}`;
}

export async function get(
  apiRoute: string,
  isSecured = true,
  reqConfig = API.defaultReqConfig,
  apiVersion: string | undefined = defaultVersion
): Promise<Axios.AxiosResponse> {
  const uri = buildUri(apiRoute, apiVersion);
  if (isSecured) await addAuthorizationHeader(reqConfig);
  return API.get(uri, reqConfig);
}

export async function post(
  apiRoute: string,
  body = {},
  isSecured = true,
  reqConfig = API.defaultReqConfig,
  apiVersion: string | undefined = undefined
): Promise<Axios.AxiosResponse> {
  const uri = buildUri(apiRoute, apiVersion);
  if (isSecured) await addAuthorizationHeader(reqConfig);
  reqConfig.data = body;
  return API.post(uri, {}, reqConfig);
}

export const parseError = async (
  error: Axios.AxiosError
): Promise<ErrorDataResponse> => {
  const { response } = error;
  let res = { ...ErrorDataResponseDefault };
  if (response) {
    if (response.status === 401) res = { ...Error401 };
    else if (response.status === 403) res = { ...Error403 };
    else if (response.status === 404) res = { ...Error404 };
    else if (response.status === 500) res = { ...Error500 };
    else if (response.status === 503 || response.status === 400) {
      let data = response.data as ErrorDataResponse;
      if (error.request.responseType === 'blob') {
        try {
          data = (await convertBlobToJson(
            response.data as Blob
          )) as ErrorDataResponse;
        } finally {
          if (data) res = data;
        }
      } else res = data;
    }
  }
  return Promise.reject(res);
};

export const parseResponseWithAttachment = (
  res: Axios.AxiosResponse,
  defFile = DownloadableFileDefault
) => {
  Logger.info(['ParseAttachment', res, res.data.type], LogArea.Api);
  const file: IDownloadableFile = { ...defFile };

  const contentDisposition = res.headers['content-disposition'];
  if (!contentDisposition) {
    Logger.warn(
      'content-disposition is not exposed from the API',
      LogArea.Api
    );
  } else {
    const fileNameMatch = contentDisposition.match(/filename=(.+);/);
    if (fileNameMatch && fileNameMatch.length === 2)
      file.filename = fileNameMatch[1];
  }
  file.data = res.data;

  return Promise.resolve(file);
};

export const downloadFile = (
  url: string,
  body = {},
  fileBase = DownloadableFileDefault
): MyKeysPromise<IDownloadableFile> => {
  const reqConfig: Axios.AxiosRequestConfig = {
    ...API.defaultReqConfig,
    responseType: 'blob',
  };
  return post(url, body, true, reqConfig)
    .then((res) => parseResponseWithAttachment(res, fileBase))
    .catch(parseError);
};
